import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Grid2,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { useNavigation } from "@refinedev/core";
import { ListButton } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { COUNTRY_CODES, iso3ToIso2 } from "./countryCodes";

export const CreatePort = () => {
  const {
    saveButtonProps,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      resource: "AdminPorts/AddNewPort",
      successNotification: () => ({
        message: `Successfully created port`,
        description: "The port was created!",
        type: "success",
      }),
      errorNotification: (error) => ({
        message: error?.message ?? "An unknown error happened",
        description: "The port could not be created!",
        type: "error",
      }),
    },
  });

  const { list } = useNavigation();

  /** Watch selected country code */
  const countryCode = useWatch({ control, name: "countryCode" });
  const iso2 = iso3ToIso2(countryCode);
  const prefix = iso2 ? `${iso2}-` : "";

  return (
    <Container maxWidth="md">
      <Grid2 size={{ xs: 12 }} container direction="column" spacing={2}>
        <ListButton
          variant="outlined"
          sx={{
            borderColor: "GrayText",
            color: "GrayText",
            backgroundColor: "transparent",
          }}
          startIcon={<ArrowBack />}
        />
        <Divider />
        <Typography variant="h5">Create port</Typography>

        <Box
          component="form"
          sx={{
            outline: "1px solid",
            outlineColor: "divider",
            borderRadius: 1,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Name */}
          <Controller
            name="name"
            control={control}
            defaultValue=""
            rules={{ required: "Name is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                required
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message as string}
              />
            )}
          />

          {/* Country Code (Autocomplete) */}
          <Controller
            name="countryCode"
            control={control}
            rules={{ required: "Country code is required" }}
            render={({ field }) => (
              <Autocomplete
                options={COUNTRY_CODES}
                getOptionLabel={(option) => `${option.alpha3} — ${option.name}`}
                onChange={(_, value) => {
                  field.onChange(value?.alpha3 ?? "");

                  // Auto-prefill port code prefix
                  const iso2 = value?.alpha2;
                  if (iso2) {
                    const currentCode = getValues("code") ?? "";
                    const suffix = currentCode.split("-")[1] ?? "";
                    setValue("code", `${iso2}-${suffix}`);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country code"
                    required
                    size="small"
                    error={!!errors.countryCode}
                    helperText={errors.countryCode?.message as string}
                  />
                )}
              />
            )}
          />

          {/* Port Code with locked prefix */}
          <Controller
            name="code"
            control={control}
            defaultValue=""
            rules={{
              required: "Port code is required",
              validate: (value) =>
                !prefix || value.startsWith(prefix)
                  ? true
                  : `Port code must start with ${prefix}`,
            }}
            render={({ field }) => {
              const suffix = prefix
                ? field.value?.replace(prefix, "")
                : field.value ?? "";

              return (
                <TextField
                  label="Port code"
                  required
                  size="small"
                  error={!!errors.code}
                  helperText={errors.code?.message as string}
                  value={suffix}
                  onChange={(e) => {
                    const newSuffix = e.target.value;
                    field.onChange(prefix + newSuffix);
                  }}
                  InputProps={{
                    startAdornment: prefix ? (
                      <Typography
                        sx={{
                          mr: 0.5,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {prefix}
                      </Typography>
                    ) : undefined,
                  }}
                />
              );
            }}
          />

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Button
              variant="text"
              color="inherit"
              onClick={() => list("ports")}
            >
              Cancel
            </Button>
            <Button {...saveButtonProps} variant="contained">
              Save
            </Button>
          </Stack>
        </Box>
      </Grid2>
    </Container>
  );
};
